import { motion } from "framer-motion";
import { useCallback, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import {
  DetailTerminal,
  createMacDetail,
  type DetailPayload,
} from "../ui/DetailTerminal";
import { monoInk } from "../../lib/mono";
import { FaDatabase } from "react-icons/fa";
import { LuCable } from "react-icons/lu";
import {
  SiBun,
  SiCloudflare,
  SiCss,
  SiDocker,
  SiDrizzle,
  SiElectron,
  SiFedora,
  SiGo,
  SiHtml5,
  SiMariadb,
  SiNextdotjs,
  SiNodedotjs,
  SiNpm,
  SiOpenjdk,
  SiPhpmyadmin,
  SiPostgresql,
  SiPostman,
  SiPrisma,
  SiPython,
  SiReact,
  SiTailscale,
  SiTypescript,
  SiUbuntu,
  SiVite,
} from "react-icons/si";
import { TbApi } from "react-icons/tb";
import type { IconType } from "react-icons";

type Experience = {
  role: string;
  company: string;
  period: string;
  description: string;
  tags: string[];
  logo: string;
};

const getExperiences = (t: (key: string) => string): Experience[] => [
  {
    role: t("exp.e1.role"),
    company: t("exp.e1.company"),
    period: t("exp.e1.period"),
    description: t("exp.e1.desc"),
    tags: ["Go", "Python", "TypeScript"],
    logo: "https://pub-ce51ac63869342f9923b7d402e1edf35.r2.dev/vdo/cosilogo.png",
  },
];

const ElysiaIcon: IconType = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden {...props}>
    <path d="M8.28477 8.29093L4.07967 15.8326C4.0588 15.87 4.00807 15.877 3.97784 15.8467L3.89306 15.7616L3.53857 15.3575L3.31262 15.0642C3.09838 14.786 2.9071 14.4908 2.74069 14.1815L2.70852 14.1217C2.53175 13.7931 2.38368 13.4497 2.26598 13.0955L2.15283 12.7549C1.98284 12.1367 1.94382 11.4896 2.03829 10.8554L2.04228 10.8286C2.08367 10.5507 2.14972 10.2771 2.23963 10.011L2.42675 9.45716L2.56641 9.16247C2.78298 8.70544 3.08158 8.29235 3.44727 7.94379L3.62468 7.77474C3.99145 7.42518 4.43749 7.17038 4.92431 7.03236C4.81046 7.06463 4.69424 7.08784 4.57675 7.10177L4.34424 7.12935L4.16699 7.14552L3.97363 7.17785L3.78027 7.19599L3.65136 7.21018L3.55851 7.216C3.52867 7.21787 3.51465 7.17969 3.53856 7.16168L3.66836 7.09928C4.08664 6.89822 4.43049 6.56913 4.65039 6.15943L4.79372 5.93512C4.96463 5.66762 5.19202 5.44104 5.45979 5.27137L5.51512 5.23632C5.61509 5.17299 5.72179 5.1211 5.83326 5.08162L6.34054 4.90199C6.43406 4.86889 6.50954 4.79798 6.54863 4.70652L6.55636 4.68838C6.57447 4.64593 6.58179 4.59964 6.57763 4.55366C6.57135 4.48434 6.53938 4.41993 6.48804 4.37311L6.39887 4.29178C6.39339 4.28677 6.38837 4.28125 6.38392 4.2753L5.81054 3.50831L5.35302 2.87565C5.2625 2.75045 5.20981 2.60176 5.20127 2.44732C5.19988 2.42285 5.22518 2.40588 5.24722 2.41649L5.74226 2.65487C6.17064 2.86116 6.65907 2.90454 7.11691 2.77695C7.22336 2.74729 7.33209 2.72669 7.44198 2.71539L7.66921 2.692C8.24062 2.63321 8.81793 2.68082 9.37209 2.83245L9.41992 2.84553L9.96126 3.04104C10.6403 3.28629 11.1784 3.8173 11.4341 4.4944L11.5476 4.93843C11.5792 5.06235 11.624 5.18256 11.681 5.29698C11.815 5.5659 12.014 5.79697 12.2598 5.9691L12.5486 6.17139C12.6755 6.2603 12.8088 6.33977 12.9472 6.40925L13.5865 6.72991C13.6151 6.74426 13.6237 6.78121 13.6043 6.80679C13.428 7.0395 13.1952 7.22295 12.9281 7.33966L12.5579 7.50141C12.4104 7.56582 12.2585 7.61916 12.1032 7.66103L11.8312 7.73432C11.4499 7.83707 11.0569 7.88912 10.6621 7.88912H10.0107C9.87586 7.88912 9.74222 7.86368 9.61676 7.81408C9.11638 7.61632 8.54732 7.82003 8.28477 8.29093Z" />
  </svg>
);

const SKILL_GROUPS: { label: string; items: { name: string; icon: IconType }[] }[] = [
  {
    label: "about.skills.langs",
    items: [
      { name: "Go", icon: SiGo },
      { name: "Python", icon: SiPython },
      { name: "TypeScript", icon: SiTypescript },
      { name: "Java", icon: SiOpenjdk },
      { name: "SQL", icon: FaDatabase },
      { name: "HTML5", icon: SiHtml5 },
      { name: "CSS3", icon: SiCss },
    ],
  },
  {
    label: "about.skills.frontend",
    items: [
      { name: "React", icon: SiReact },
      { name: "Next.js", icon: SiNextdotjs },
      { name: "Vite", icon: SiVite },
      { name: "Electron", icon: SiElectron },
    ],
  },
  {
    label: "about.skills.backend",
    items: [
      { name: "Node.js", icon: SiNodedotjs },
      { name: "Bun", icon: SiBun },
      { name: "npm", icon: SiNpm },
      { name: "Elysia", icon: ElysiaIcon },
      { name: "REST API", icon: TbApi },
      { name: "WebSocket", icon: LuCable },
      { name: "Postman", icon: SiPostman },
    ],
  },
  {
    label: "about.skills.data",
    items: [
      { name: "PostgreSQL", icon: SiPostgresql },
      { name: "MariaDB", icon: SiMariadb },
      { name: "Prisma", icon: SiPrisma },
      { name: "Drizzle", icon: SiDrizzle },
      { name: "phpMyAdmin", icon: SiPhpmyadmin },
    ],
  },
  {
    label: "about.skills.platform",
    items: [
      { name: "Docker", icon: SiDocker },
      { name: "Cloudflare", icon: SiCloudflare },
      { name: "Tailscale", icon: SiTailscale },
      { name: "Ubuntu", icon: SiUbuntu },
      { name: "Fedora", icon: SiFedora },
    ],
  },
];

function SkillIcons({
  items,
  ink,
  muted,
  chip,
  hairline,
}: {
  items: { name: string; icon: IconType }[];
  ink: string;
  muted: string;
  chip: string;
  hairline: string;
}) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-3.5">
      {items.map(({ name, icon: Icon }) => (
        <span key={name} className="flex min-w-14 flex-col items-center gap-1.5 text-center">
          <span
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl"
            style={{
              color: ink,
              background: chip,
              border: `1px solid ${hairline}`,
            }}
          >
            <Icon className="size-[18px]" aria-hidden />
          </span>
          <span className="text-[10px] leading-none whitespace-nowrap" style={{ color: muted }}>
            {name}
          </span>
        </span>
      ))}
    </div>
  );
}

const ABOUT_PHOTOS: { src: string; alt: string; className: string; fit?: string }[] = [
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/A1FDB7E8-5053-4F2E-A203-0D624155BC40.jpg",
    alt: "Charcrith taking a mirror selfie in a black t-shirt and jeans",
    className:
      "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:aspect-auto",
  },
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/IMG_7729-2.jpg",
    alt: "Charcrith outdoors in a black t-shirt with trees behind him",
    className:
      "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-3 lg:row-span-2 lg:row-start-1 lg:aspect-auto",
  },
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/20251223_023221849_iOS.jpg",
    alt: "Charcrith taking a mirror selfie in a black t-shirt",
    className:
      "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-4 lg:row-span-2 lg:row-start-1 lg:aspect-auto",
  },
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/20251224_122912654_iOS.jpg",
    alt: "Night view with a lit roof against a dark sky",
    className: "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-2 lg:row-start-3",
    fit: "object-contain object-center",
  },
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/20260104_023729842_iOS.jpg",
    alt: "Mount Fuji across the water under a clear sky",
    className: "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-3 lg:row-start-3",
    fit: "object-contain object-center",
  },
  {
    src: "https://pub-0ef82e6e9f8f443387b8c7a7e1023afd.r2.dev/aboutme/IMG_8800.jpg",
    alt: "Clock tower and neon signs at night",
    className: "col-span-2 aspect-[3/4] lg:col-span-1 lg:col-start-4 lg:row-start-3",
    fit: "object-contain object-center",
  },
];

function PhotoSlot({
  src,
  alt,
  className,
  fit = "object-cover object-[center_22%]",
  fill,
  hairline,
}: {
  src: string;
  alt: string;
  className?: string;
  fit?: string;
  fill: string;
  hairline: string;
}) {
  return (
    <div
      className={`relative overflow-hidden ${className ?? ""}`}
      style={{ background: fill, boxShadow: `inset 0 0 0 1px ${hairline}` }}
    >
      <img
        src={src}
        alt={alt}
        className={`absolute inset-0 h-full w-full ${fit}`}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function SkillPills({
  tags,
  muted,
  chip,
  hairline,
}: {
  tags: string[];
  muted: string;
  chip: string;
  hairline: string;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {tags.map((tag) => (
        <span
          key={tag}
          className="text-[12px] px-3 py-1 rounded-full"
          style={{
            color: muted,
            background: chip,
            border: `1px solid ${hairline}`,
          }}
        >
          {tag}
        </span>
      ))}
    </div>
  );
}

function ExperienceTimelineItem({
  exp,
  ink,
  muted,
  hairline,
  isDark,
  onOpen,
}: {
  exp: Experience;
  ink: string;
  muted: string;
  hairline: string;
  isDark: boolean;
  onOpen: (d: DetailPayload) => void;
}) {
  const open = () =>
    onOpen(
      createMacDetail({
        kind: "experience",
        title: exp.role,
        description: exp.description,
        fields: {
          company: exp.company,
          period: exp.period,
        },
        tags: exp.tags,
        image: exp.logo,
      }),
    );

  return (
    <motion.article
      whileTap={{ scale: 0.99 }}
      transition={{ type: "spring", stiffness: 420, damping: 32 }}
      className="min-w-0 mb-8 sm:mb-10 rounded-[18px] overflow-hidden cursor-pointer select-none last:mb-0"
      style={{
        border: `1px solid ${hairline}`,
        background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
      }}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div
        className="flex items-center gap-3 px-4 py-3.5 sm:px-5 sm:py-4"
        style={{ borderBottom: `1px solid ${hairline}` }}
      >
        <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-[10px] overflow-hidden bg-white p-1.5">
          <img src={exp.logo} alt={exp.company} className="w-full h-full object-contain" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3
              className="text-[15px] sm:text-[17px] font-semibold leading-snug"
              style={{ color: ink }}
            >
              {exp.role}
            </h3>
            <span
              className="shrink-0 text-[11px] px-2 py-0.5 rounded-full tabular-nums"
              style={{
                color: ink,
                background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              }}
            >
              {exp.period}
            </span>
          </div>
          <p className="text-[13px] mt-0.5" style={{ color: muted }}>
            {exp.company}
          </p>
        </div>
      </div>

      <div className="px-4 py-3.5 sm:px-5 sm:py-4 space-y-3">
        <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: muted }}>
          {exp.description}
        </p>
        <SkillPills
          tags={exp.tags}
          muted={muted}
          chip={isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}
          hairline={hairline}
        />
      </div>
    </motion.article>
  );
}

export const About = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === "dark";
  const EXPERIENCES = getExperiences(t);
  const { ink, muted, hairline, card, chip } = monoInk(isDark);
  const slotFill = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)";
  const [detail, setDetail] = useState<DetailPayload | null>(null);
  const closeDetail = useCallback(() => setDetail(null), []);

  return (
    <>
      <DetailTerminal detail={detail} onClose={closeDetail} />

      <section id="about" className="apple-tile apple-tile-nearblack overflow-x-clip !pb-8 sm:!pb-10">
        <div className="mx-auto w-full max-w-[1120px] space-y-10 sm:space-y-14">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55 }}
            className="grid grid-cols-6 gap-2.5 sm:gap-3 lg:grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))] lg:grid-rows-[auto_auto_auto] lg:gap-3"
          >
            <h2
              className="col-span-6 self-end text-[clamp(3rem,8vw,5.5rem)] font-semibold leading-[0.9] tracking-[-0.045em] lg:col-span-1 lg:col-start-1 lg:row-start-1"
              style={{ color: ink }}
            >
              {t("about.title1")}
              {t("about.title2")}
            </h2>

            <div className="col-span-6 space-y-3 lg:col-span-1 lg:col-start-1 lg:row-start-2 lg:self-end lg:pb-1">
              <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: muted }}>
                {t("about.desc1")}
                <span style={{ color: ink, fontWeight: 600 }}>{t("about.desc1.bu")}</span>
                {t("about.desc1.spec")}
                <span style={{ color: ink, fontWeight: 600 }}>{t("about.desc1.ds")}</span>
                {t("about.desc1.and")}
                <span style={{ color: ink, fontWeight: 600 }}>{t("about.desc1.cyber")}</span>
              </p>
              <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: muted }}>
                {t("about.desc2")}
              </p>
              <p className="text-[13px] font-medium tracking-wide pt-1" style={{ color: ink }}>
                {t("about.focus")}
              </p>
            </div>

            {ABOUT_PHOTOS.map((photo) => (
              <PhotoSlot
                key={photo.src}
                src={photo.src}
                alt={photo.alt}
                fit={photo.fit}
                fill={slotFill}
                hairline={hairline}
                className={photo.className}
              />
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="space-y-10"
          >
            <div
                className="max-w-xl rounded-[18px] p-4 sm:p-5 flex gap-3.5 sm:gap-4 items-start"
                style={{
                  background: card,
                  border: `1px solid ${hairline}`,
                }}
              >
                <div
                  className="shrink-0 w-14 h-14 rounded-[12px] overflow-hidden bg-white flex items-center justify-center p-2"
                  style={{ border: `1px solid ${hairline}` }}
                >
                  <img
                    src="https://pub-ce51ac63869342f9923b7d402e1edf35.r2.dev/vdo/BU%20logo.png"
                    alt="Bangkok University"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="space-y-1.5 pt-0.5 min-w-0 flex-1">
                  <p
                    className="text-[11px] uppercase tracking-[0.12em] font-semibold"
                    style={{ color: muted }}
                  >
                    {t("about.edu.label")}
                  </p>
                  <h3
                    className="text-[17px] sm:text-[21px] font-semibold tracking-tight leading-snug"
                    style={{ color: ink }}
                  >
                    {t("about.edu.bu")}
                  </h3>
                  <p className="text-[13px] sm:text-[14px] leading-relaxed" style={{ color: muted }}>
                    {t("about.edu.fac")}
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-medium pt-1" style={{ color: muted }}>
                    {t("about.edu.major")}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div
                  className="text-[12px] uppercase tracking-[0.14em] font-semibold px-0.5"
                  style={{ color: muted }}
                >
                  {t("about.skills1")}
                  {t("about.skills2")}
                </div>

                <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
                  {SKILL_GROUPS.map((group) => (
                    <div key={group.label} className="space-y-2">
                      <p className="text-[11px] uppercase tracking-[0.1em] font-medium" style={{ color: muted }}>
                        {t(group.label)}
                      </p>
                      <SkillIcons items={group.items} ink={ink} muted={muted} chip={chip} hairline={hairline} />
                    </div>
                  ))}
                </div>
              </div>
          </motion.div>
        </div>
      </section>

      <section id="experience" className="apple-tile apple-tile-nearblack overflow-x-clip !pt-8 sm:!pt-10">
        <div className="apple-container space-y-6 sm:space-y-10">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.55 }}
            className="apple-title text-center px-1"
            style={{ color: ink }}
          >
            {t("exp.title1")}
            {t("exp.title2")}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="max-w-[680px] mx-auto"
          >
            {EXPERIENCES.map((exp) => (
              <ExperienceTimelineItem
                key={exp.company}
                exp={exp}
                ink={ink}
                muted={muted}
                hairline={hairline}
                isDark={isDark}
                onOpen={setDetail}
              />
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};
